import logoShort from "@/assets/images/logo_short.png";
import { SidebarLogo } from "@/entities/layout/ui/SidebarLogo";
import { useApps } from "@/shared/hooks/useApps";
import { GamepadIcon, HomeIcon } from "@/shared/ui/icons/MenuIcons";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { UserProfile } from "../UserProfile";
import styles from "./GamesSidebar.module.css";

interface GamesSidebarProps {
  logoAlt: string;
}

export const GamesSidebar: React.FC<GamesSidebarProps> = ({ logoAlt }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { data: apps, isLoading: isAppsLoading } = useApps();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <aside
      className={`${styles.sidebar} ${
        isCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded
      }`}
    >
      <div className={styles.logoContainer}>
        <SidebarLogo
          url="/"
          alt={isCollapsed ? "DI" : logoAlt}
          imageSrc={isCollapsed ? logoShort : undefined}
        />
        <button
          onClick={toggleSidebar}
          className={styles.collapseButton}
          title={isCollapsed ? "Развернуть" : "Свернуть"}
        >
          <svg
            className={`${styles.collapseIcon} ${
              isCollapsed ? styles.collapseIconRotated : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      <nav className={styles.nav}>
        <div
          className={`${styles.navContent} ${
            isCollapsed ? styles.navContentCollapsed : styles.navContentExpanded
          }`}
        >
          <div className={styles.section}>
            {!isCollapsed && <h3 className={styles.sectionTitle}>Основное</h3>}
            <nav className={styles.navLinks}>
              <Link
                to="/"
                className={`${styles.navLink} ${
                  isCollapsed ? styles.navLinkCollapsed : styles.navLinkExpanded
                }`}
                title={isCollapsed ? "Главная" : undefined}
              >
                <HomeIcon className={styles.navLinkIcon} />
                {!isCollapsed && (
                  <span className={styles.navLinkText}>Главная</span>
                )}
              </Link>
            </nav>
          </div>

          <div className={styles.section}>
            {!isCollapsed && <h3 className={styles.sectionTitle}>Игры</h3>}
            <nav className={styles.navLinks}>
              {isAppsLoading ? (
                <div className={styles.skeletonContainer}>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`${styles.skeletonItem} ${
                        isCollapsed
                          ? styles.skeletonItemCollapsed
                          : styles.skeletonItemExpanded
                      }`}
                    >
                      <div className={styles.skeletonIcon}></div>
                      {!isCollapsed && (
                        <div className={styles.skeletonText}></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : apps && apps.length > 0 ? (
                <>
                  {apps.slice(0, 8).map((app) => (
                    <Link
                      key={app.id}
                      to={`/game-info/${app.slug}`}
                      className={`${styles.navLink} ${
                        isCollapsed
                          ? styles.navLinkCollapsed
                          : styles.navLinkExpanded
                      }`}
                      title={isCollapsed ? app.title : app.short_description}
                    >
                      <div className={styles.navLinkIcon}>
                        <GamepadIcon className={styles.navLinkIcon} />
                      </div>
                      {!isCollapsed && (
                        <span className={styles.navLinkText}>{app.title}</span>
                      )}
                    </Link>
                  ))}
                  {!isCollapsed && apps.length > 8 && (
                    <Link to="/games" className={styles.showAllLink}>
                      <span className={styles.showAllLinkText}>
                        Показать все ({apps.length})
                      </span>
                    </Link>
                  )}
                </>
              ) : (
                !isCollapsed && (
                  <div className={styles.emptyState}>Игры не найдены</div>
                )
              )}
            </nav>
          </div>
        </div>
      </nav>

      <UserProfile isCollapsed={isCollapsed} />
    </aside>
  );
};
